import { ArrowBackIos, ArrowForwardIos, Check as CheckIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { memo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { neutral } from '../../../styles/mui/colors';
import useAppSelector from '../../../util/hooks/useAppSelector';
import useScreenSize from '../../../util/hooks/useScreenSize';
import Link from '../../Links/Link/Link';
// Side-effect import: this module reads the StoryblokPage redux slice, which is injected
// lazily by the duck. Importing it keeps the reducer in whatever chunk this module lands in.
import '../../../containers/StoryblokPage/StoryblokPage.duck';

const PassTierCardsPresetSkeleton = () => {
  const { isMobile } = useScreenSize();
  const cardsData = Array.from({ length: 5 }, () => ({ features: Array.from({ length: 5 }) }));

  return isMobile ? (
    <Box
      sx={{
        flexGrow: 1,
        p: { xs: 0, sm: 2, md: 4 },
      }}
    >
      <Grid container spacing={4}>
        {cardsData.map((card, index) => (
          <Grid key={index} size={{ md: 3, sm: 6, xs: 12 }}>
            <Card
              sx={{
                position: 'relative',
                borderColor: 'grey.300',
                borderWidth: 2,
                borderStyle: 'solid',
                boxShadow: 1,
              }}
            >
              <Skeleton
                animation="wave"
                height={390}
                sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                variant="rectangular"
                width="100%"
              />
              <CardContent sx={{ pt: 2 }}>
                <Skeleton animation="wave" height={40} variant="text" />
                <Skeleton animation="wave" height={80} variant="text" />
                <Skeleton animation="wave" height={20} variant="text" />

                {card.features && card.features.length > 0 ? (
                  <List dense>
                    {card.features.map((_, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <Stack direction="column" spacing={0} width="100%">
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                ) : null}
              </CardContent>
              <Skeleton animation="wave" height={50} variant="text" />
              <Skeleton animation="wave" height={50} variant="text" />
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  ) : (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', py: 4 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          transition: 'transform 0.5s ease',
        }}
      >
        {cardsData.map((card, index) => (
          <Box
            key={index}
            sx={{
              flex: '0 0 calc((100% - 48px) / 3)', // Approximate for skeleton (gap 24px * 2)
              boxSizing: 'border-box',
            }}
          >
            <Card
              sx={{
                position: 'relative',
                borderColor: 'grey.300',
                borderWidth: 2,
                borderStyle: 'solid',
                boxShadow: 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Skeleton
                animation="wave"
                height={290}
                sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                variant="rectangular"
                width="100%"
              />
              <CardContent sx={{ pt: 2, flexGrow: 1 }}>
                <Skeleton animation="wave" height={40} variant="text" />
                <Skeleton animation="wave" height={80} variant="text" />
                <Skeleton animation="wave" height={20} variant="text" />

                {card.features && card.features.length > 0 ? (
                  <List dense>
                    {card.features.map((_, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <Stack direction="column" spacing={0} width="100%">
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                          <Skeleton animation="wave" height={20} variant="text" width="100%" />
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                ) : null}
              </CardContent>

              <Box sx={{ px: 2, pb: 2 }}>
                <Skeleton animation="wave" height={50} variant="text" />
                <Skeleton animation="wave" height={50} variant="text" />
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const PassTierCardsPresetComponent = ({ blok }) => {
  const theme = useTheme();
  const { dataSource } = blok;

  const { fetchStoryPresetsInProgress } = useAppSelector(state => ({
    fetchStoryPresetsInProgress: state.StoryblokPage.fetchStoryPresetsInProgress,
  }), shallowEqual);

  const { isMobile, isDesktop } = useScreenSize();

  const [slideIndex, setSlideIndex] = useState(0);

  if (fetchStoryPresetsInProgress) return <PassTierCardsPresetSkeleton />;

  const productsData = dataSource && typeof dataSource === 'object' ? Object.values(dataSource) : [];

  const checkEmpty = (value) => {
    if (typeof value === 'string') {
      const sanitizedValue = value.replace(/[\u200B-\u200D\uFEFF\u200E\u200F]/g, '').trim();
      return sanitizedValue === 'empty' || sanitizedValue === '' ? null : sanitizedValue;
    }
    return value === undefined || value === null ? null : value;
  };

  const getFeaturesArray = (featuresString) => {
    if (typeof featuresString === 'string') {
      const trimmedString = featuresString.trim();
      if (trimmedString.toLowerCase() === 'empty' || trimmedString === '') return [];
      return trimmedString.includes(';')
        ? trimmedString.split(';')
          .map((item) => item.trim())
          .filter((item) => item.toLowerCase() !== 'empty' && item !== '')
        : [trimmedString];
    }
    return [];
  };

  const getSlugAndRest = (slugString) => {
    if (typeof slugString === 'string' && slugString.includes('/')) {
      const [slug, rest] = slugString.split('/');
      return [checkEmpty(slug) || '', checkEmpty(rest) || null];
    } else {
      return [checkEmpty(slugString) || '', null];
    }
  };

  const getProductData = (productData) => {
    if (!productData || Object.keys(productData).length === 0) return null;

    const {
      imageUrl,
      chip,
      heading,
      price,
      oldPrice,
      subPriceHeading,
      features,
      buttonLabel,
      buttonLink,
      subButtonLabel,
      subButtonSlug,
      subButtonLink,
      isBestseller,
    } = productData;

    const [slug, rest] = getSlugAndRest(subButtonSlug) || ['', ''];

    return {
      image: checkEmpty(imageUrl),
      chip: checkEmpty(chip),
      title: checkEmpty(heading),
      price: checkEmpty(price),
      oldPrice: checkEmpty(oldPrice),
      subPriceHeading: checkEmpty(subPriceHeading),
      features: getFeaturesArray(features),
      buttonLabel: checkEmpty(buttonLabel),
      buttonLink: checkEmpty(buttonLink),
      subButtonLabel: checkEmpty(subButtonLabel),
      subButtonLink: checkEmpty(subButtonLink),
      subButtonSlug: slug,
      subButtonRest: rest,
      isBestseller: isBestseller === 'true' || isBestseller === true,
    };
  };

  const cardsData = productsData
    .map((data) => getProductData(data))
    .filter((card) => card !== null);

  // Three cards do not fit between 900 and 1199 px (clipped price, arrow over the third card).
  const visibleCount = isDesktop ? 3 : 2;
  const gap = theme.spacing(3);
  const cardWidthCalc = `calc((100% - ${gap} * ${visibleCount - 1}) / ${visibleCount})`;
  const stepCalc = `calc(${cardWidthCalc} + ${gap})`;

  const handlePrev = () => {
    setSlideIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const handleNext = () => {
    setSlideIndex((prevIndex) => Math.min(prevIndex + 1, cardsData.length - visibleCount));
  };

  const sortedCardsData = isMobile
    ? [...cardsData].sort((a, b) => (b.isBestseller ? 1 : 0) - (a.isBestseller ? 1 : 0))
    : cardsData;

  return isMobile ? (
    <Box sx={{ flexGrow: 1, p: { xs: 0, sm: 2, md: 4 } }}>
      <Grid container spacing={4}>
        {sortedCardsData.map((card, index) => (
          <Grid key={index} size={{ md: 3, sm: 6, xs: 12 }}>
            <Card
              sx={{
                position: 'relative',
                borderColor: card.isBestseller ? 'primary.main' : 'grey.300',
                borderWidth: 2,
                borderStyle: 'solid',
                boxShadow: card.isBestseller ? 4 : 1,
              }}
            >
              {card.isBestseller ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: 'primary.main',
                    color: 'secondary.main',
                    py: 1,
                    textAlign: 'center',
                  }}
                >
                  Bestseller
                </Box>
              ) : null}
              {card.image ? (
                <CardMedia
                  alt={card.title}
                  component="img"
                  src={card.image}
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                />
              ) : null}

              <CardContent sx={{ pt: 2 }}>
                {card.chip ? (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Chip label={card.chip} />
                  </Box>
                ) : null}

                {card.title ? (
                  <Typography component="div" gutterBottom textAlign="center" variant="h5">
                    {card.title}
                  </Typography>
                ) : null}

                {card.price ? (
                  <Box alignItems="center" display="flex" justifyContent="center" width={1}>
                    <Typography color="text.primary" component="h4" textAlign="center" variant="h3">
                      {card.price}
                    </Typography>
                    {card.oldPrice ? (
                      <Typography
                        color="text.secondary"
                        component="h4"
                        sx={{ textDecoration: 'line-through', ml: 1 }}
                        variant="h5"
                      >
                        {card.oldPrice}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                {card.subPriceHeading ? (
                  <Typography color="text.secondary" gutterBottom textAlign="center" variant="body2">
                    {card.subPriceHeading}
                  </Typography>
                ) : null}

                {card.features && card.features.length > 0 ? (
                  <List dense>
                    {card.features.map((feature, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                ) : null}
              </CardContent>

              {card.buttonLink ? (
                <CardActions sx={{ justifyContent: 'center' }}>
                  <Button color="primary" fullWidth href={card.buttonLink} size="large" variant="contained">
                    {card.buttonLabel}
                  </Button>
                </CardActions>
              ) : null}

              {card.subButtonLink ? (
                <Button color="primary" fullWidth href={card.subButtonLink} variant="text">
                  <Typography color={neutral[400]} variant="body2">
                    {card.subButtonLabel}
                  </Typography>
                </Button>
              ) : card.subButtonSlug ? (
                <Button
                  color="primary"
                  component={Link}
                  fullWidth
                  to={{
                    name: 'StoryblokPage',
                    params: {
                      slug: card.subButtonSlug,
                      ...(card.subButtonRest ? { rest: card.subButtonRest } : {}),
                    },
                  }}
                  variant="text"
                >
                  <Typography color={neutral[400]} variant="body2">
                    {card.subButtonLabel}
                  </Typography>
                </Button>
              ) : null}
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  ) : (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', py: 4 }}>
      {/* Slider Buttons */}
      {slideIndex > 0 ? (
        <IconButton
          aria-label="back button"
          onClick={handlePrev}
          sx={{ position: 'absolute', top: '50%', left: 0, zIndex: 1, transform: 'translateY(-50%)' }}
        >
          <ArrowBackIos />
        </IconButton>
      ) : null}
      {slideIndex < cardsData.length - visibleCount ? (
        <IconButton
          aria-label="next button"
          onClick={handleNext}
          sx={{ position: 'absolute', top: '50%', right: 0, zIndex: 1, transform: 'translateY(-50%)' }}
        >
          <ArrowForwardIos />
        </IconButton>
      ) : null}

      {/* Slider Container */}
      <Box
        style={{
          transform: `translateX(calc(-${slideIndex} * ${stepCalc}))`,
        }}
        sx={{
          display: 'flex',
          gap: 3,
          transition: 'transform 0.5s ease',
        }}
      >
        {cardsData.map((card, index) => (
          <Box
            key={index}
            sx={{
              flex: `0 0 ${cardWidthCalc}`,
              boxSizing: 'border-box',
            }}
          >
            <Card
              sx={{
                position: 'relative',
                borderColor: card.isBestseller ? 'primary.main' : 'grey.300',
                borderWidth: 2,
                borderStyle: 'solid',
                boxShadow: card.isBestseller ? 4 : 1,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {card.image ? (
                <CardMedia
                  alt={card.title}
                  component="img"
                  src={card.image}
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                />
              ) : null}

              {card.chip ? (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Chip label={card.chip} />
                </Box>
              ) : null}

              {card.isBestseller ? (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    backgroundColor: 'primary.main',
                    color: 'secondary.main',
                    py: 1,
                    textAlign: 'center',
                  }}
                >
                  Bestseller
                </Box>
              ) : null}

              <CardContent sx={{ pt: 2, flexGrow: 1 }}>
                {card.title ? (
                  <Typography component="div" gutterBottom textAlign="center" variant="h5">
                    {card.title}
                  </Typography>
                ) : null}

                {card.price ? (
                  <Box alignItems="center" display="flex" justifyContent="center" width={1}>
                    <Typography color="text.primary" component="h4" textAlign="center" variant="h3">
                      {card.price}
                    </Typography>
                    {card.oldPrice ? (
                      <Typography
                        color="text.secondary"
                        component="h4"
                        sx={{ textDecoration: 'line-through', ml: 1 }}
                        variant="h5"
                      >
                        {card.oldPrice}
                      </Typography>
                    ) : null}
                  </Box>
                ) : null}

                {card.subPriceHeading ? (
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    sx={{ mb: 2 }}
                    textAlign="center"
                    variant="body1"
                  >
                    {card.subPriceHeading}
                  </Typography>
                ) : null}

                {card.features && card.features.length > 0 ? (
                  <List dense>
                    {card.features.map((feature, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                ) : null}
              </CardContent>

              <Box sx={{ px: 2, pb: 2 }}>
                {card.buttonLink ? (
                  <CardActions sx={{ justifyContent: 'center' }}>
                    <Button color="primary" fullWidth href={card.buttonLink} size="large" variant="contained">
                      {card.buttonLabel}
                    </Button>
                  </CardActions>
                ) : null}

                {card.subButtonLink ? (
                  <Button color="primary" fullWidth href={card.subButtonLink} variant="text">
                    <Typography color={neutral[400]} variant="body2">
                      {card.subButtonLabel}
                    </Typography>
                  </Button>
                ) : card.subButtonSlug ? (
                  <Button
                    color="primary"
                    component={Link}
                    fullWidth
                    to={{
                      name: 'StoryblokPage',
                      params: {
                        slug: card.subButtonSlug,
                        ...(card.subButtonRest ? { rest: card.subButtonRest } : {}),
                      },
                    }}
                    variant="text"
                  >
                    <Typography color={neutral[400]} variant="body2">
                      {card.subButtonLabel}
                    </Typography>
                  </Button>
                ) : null}
              </Box>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

PassTierCardsPresetComponent.displayName = 'PassTierCardsPreset';
const StoryblokPassTierCardsPreset = memo(PassTierCardsPresetComponent);

export default StoryblokPassTierCardsPreset;